import { FormikHelpers, Formik, Form } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";

import {
  Card,
  CardHeader,
  CardContent,
  MenuItem,
  makeStyles
} from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { FormikSelect } from "src/components/form/Select";
import SubmitButton from "src/components/form/SubmitButton";
import { TenantFields_featureFlags } from "src/graphql/generated/TenantFields";
import {
  TenantFeatureFlagsInput,
  TenantFeatureFlagsWithDefaultsInput,
  FeatureToggleType
} from "src/graphql/generated/globalTypes";
import {
  tenantEditFeatureFlagsWithDefaults,
  tenantEditFeatureFlagsWithDefaultsVariables
} from "src/graphql/generated/tenantEditFeatureFlagsWithDefaults";
import { EDIT_TENANT_DEFAULT_FEATURE_FLAGS } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";

type Values = TenantFeatureFlagsWithDefaultsInput;

const useStyles = makeStyles(() => ({
  toggleSelectWrapper: {
    display: "flex",
    columnGap: "1rem",
    alignItems: "center",
    marginBottom: "1rem"
  },
  toggleSelect: {
    marginBottom: 0,
    minWidth: "150px"
  }
}));

const featureFlagsWithDefaultValueToInput = (
  featureFlagsValue: TenantFields_featureFlags,
  defaultFlags: string[]
): TenantFeatureFlagsWithDefaultsInput => {
  const convertedFeatureFlags: Record<string, FeatureToggleType> = {};
  const featureFlags: TenantFeatureFlagsInput = _.omit(featureFlagsValue, [
    "__typename"
  ]);

  _.each(featureFlags, (value, key) => {
    if (defaultFlags.includes(key)) {
      convertedFeatureFlags[key] = FeatureToggleType.DEFAULT;
    } else {
      convertedFeatureFlags[key] = value
        ? FeatureToggleType.ON
        : FeatureToggleType.OFF;
    }
  });

  return convertedFeatureFlags;
};

export default function FeatureFlags(): JSX.Element {
  const tenant = useTenant()!;
  const snackbar = useSnackbar();
  const classes = useStyles();

  const [updateFeatureFlags] = useMutation<
    tenantEditFeatureFlagsWithDefaults,
    tenantEditFeatureFlagsWithDefaultsVariables
  >(EDIT_TENANT_DEFAULT_FEATURE_FLAGS, {
    onErrorSnackbarMessage: "Failed to update feature flags."
  });

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    const { errors, data } = await updateFeatureFlags({
      variables: {
        featureFlags: values
      }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Organization updated.", { variant: "info" });
      helpers.resetForm({
        values: featureFlagsWithDefaultValueToInput(
          data!.tenantEditFeatureFlagsWithDefaults.featureFlags,
          data!.tenantEditFeatureFlagsWithDefaults.defaultFeatureFlags
        )
      });
    }
  }

  return (
    <Card>
      <CardHeader title="Feature Flags" />
      <CardContent>
        <Formik
          initialValues={featureFlagsWithDefaultValueToInput(
            tenant.featureFlags,
            tenant.defaultFeatureFlags
          )}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <FormikErrorBanner />
              {Object.keys(
                featureFlagsWithDefaultValueToInput(
                  tenant.featureFlags,
                  tenant.defaultFeatureFlags
                )
              )
                .sort()
                .map(featureFlagName => (
                  <div
                    className={classes.toggleSelectWrapper}
                    key={featureFlagName}
                  >
                    <FormikSelect
                      name={featureFlagName}
                      className={classes.toggleSelect}
                    >
                      <MenuItem value={FeatureToggleType.ON}>
                        Always On
                      </MenuItem>
                      <MenuItem value={FeatureToggleType.OFF}>
                        Always Off
                      </MenuItem>
                      <MenuItem value={FeatureToggleType.DEFAULT}>
                        Default
                      </MenuItem>
                    </FormikSelect>

                    <span>{featureFlagName}</span>
                  </div>
                ))}

              <SubmitButton
                type="submit"
                disabled={!dirty}
                isSubmitting={isSubmitting}
                submitText="Update"
                loadingText="Updating..."
              />
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
